import React, { useState, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useSearchParams, useNavigate, useParams } from 'react-router-dom'
import { Alert } from '../../../components/FbUI/Alert'
import { FBPage } from '../../../components/FbUI/Page/FBPage'
import {
  FBPageHeader,
  FBPageTableContainer,
  FBPageTitle,
  FBPageTitleRow,
} from 'components/FbUI/Page/styles'
import { PillTabs } from '../../../components/FbUI/PillTabs'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { SamplesTable } from '../../../components/Samples/SamplesTable'
import { SampleRequestFormModal } from '../../../components/Samples/SampleRequestFormModal'
import { SampleRequestPreviewModal } from '../../../components/Samples/SampleRequestPreviewModal'
import { SampleRequestReviewModal } from '../../../components/Samples/SampleRequestReviewModal'
import FbButton from '../../../components/FbUI/FbButton'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import useApiService from '../../../services/api'
import { toast } from 'react-toastify'
import { useAuth } from 'context/authentication/useAuth'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'

type SamplesTab = 'all' | 'my' | 'needs-review'

export function SamplesDashboard() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const params = useParams()
  const { isLimited, user } = useAuth()
  const rawTab = (params?.type ?? (isLimited ? 'my' : 'all')) as SamplesTab
  const selectedTab = (
    isLimited && rawTab === 'all' ? 'my' : rawTab
  ) as SamplesTab
  const [searchParams, setSearchParams] = useSearchParams()
  const [showFormModal, setShowFormModal] = useState(false)
  const [editingSampleId, setEditingSampleId] = useState<number | null>(null)
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [previewingSampleId, setPreviewingSampleId] = useState<number | null>(
    null
  )
  const [showReviewModal, setShowReviewModal] = useState(false)
  const [reviewingSampleId, setReviewingSampleId] = useState<number | null>(
    null
  )
  const [totalSamples, setTotalSamples] = useState<number | null>(null)
  const [totalMySamples, setTotalMySamples] = useState<number | null>(null)
  const [totalNeedsReview, setTotalNeedsReview] = useState<number | null>(null)

  const api = useApiService()
  const { data: companyData } = api.useGetCompanyDetails()
  const { enableUserTypes } = useFeatureFlagService()

  const showNeedsReviewTab = enableUserTypes && !isLimited
  const brokerBlockedFromSubmitting =
    enableUserTypes && isLimited && !user?.rsm_assignment

  // Check for sample_request_id in URL and sync modal state
  useEffect(() => {
    const sampleRequestIdParam = searchParams.get('sample_request_id')
    const mode = searchParams.get('mode')

    if (sampleRequestIdParam) {
      // Reset all modals before opening the correct one
      const resetModals = () => {
        setShowFormModal(false)
        setEditingSampleId(null)
        setShowPreviewModal(false)
        setPreviewingSampleId(null)
        setShowReviewModal(false)
        setReviewingSampleId(null)
      }

      if (sampleRequestIdParam === 'new') {
        // Open new request modal (edit mode)
        resetModals()
        setEditingSampleId(null)
        setShowFormModal(true)
      } else {
        // Try to parse as sample ID
        const sampleId = parseInt(sampleRequestIdParam, 10)
        if (!isNaN(sampleId)) {
          resetModals()
          if (mode === 'edit') {
            setEditingSampleId(sampleId)
            setShowFormModal(true)
          } else if (mode === 'review') {
            setReviewingSampleId(sampleId)
            setShowReviewModal(true)
          } else {
            // Default to preview mode
            setPreviewingSampleId(sampleId)
            setShowPreviewModal(true)
          }
        }
      }
    } else {
      // No sample_request_id in URL, ensure modals are closed
      setShowFormModal(false)
      setEditingSampleId(null)
      setShowPreviewModal(false)
      setPreviewingSampleId(null)
      setShowReviewModal(false)
      setReviewingSampleId(null)
    }
  }, [searchParams])

  const handleNewRequest = () => {
    // Check if CS email is configured
    if (!companyData?.sample_customer_service_email) {
      toast.error(
        'Please configure a Sample Customer Service Email in Settings → Company before creating sample requests.'
      )
      return
    }
    // Update URL - useEffect will handle opening modal
    setSearchParams({ sample_request_id: 'new' })
  }

  const handlePreviewRequest = (sampleId: number) => {
    // Update URL - useEffect will handle opening preview modal
    setSearchParams({ sample_request_id: sampleId.toString() })
  }

  const handleEditRequest = (sampleId: number) => {
    // Update URL with mode=edit - useEffect will handle opening edit modal
    setSearchParams({ sample_request_id: sampleId.toString(), mode: 'edit' })
  }

  const handleCloseModal = () => {
    // Remove sample_request_id and mode from URL - preserve other params
    const newParams = new URLSearchParams(searchParams)
    newParams.delete('sample_request_id')
    newParams.delete('mode')
    setSearchParams(newParams)
  }

  const handleClosePreviewModal = () => {
    // Remove sample_request_id from URL - preserve other params
    const newParams = new URLSearchParams(searchParams)
    newParams.delete('sample_request_id')
    setSearchParams(newParams)
  }

  const handleCloseReviewModal = () => {
    const newParams = new URLSearchParams(searchParams)
    newParams.delete('sample_request_id')
    newParams.delete('mode')
    setSearchParams(newParams)
  }

  const formatTabTitle = (label: string, count: number | null) =>
    count == null ? (
      label
    ) : (
      <>
        {label}{' '}
        <span className="font-normal text-fg-secondary tabular-nums">
          ({count.toLocaleString()})
        </span>
      </>
    )

  const tabs: { key: SamplesTab; title: React.ReactNode }[] = [
    ...(!isLimited
      ? [
          {
            key: 'all' as const,
            title: formatTabTitle('All Samples', totalSamples),
          },
        ]
      : []),
    {
      key: 'my' as const,
      title: formatTabTitle('My Samples', totalMySamples),
    },
    ...(showNeedsReviewTab
      ? [
          {
            key: 'needs-review' as const,
            title: formatTabTitle('Needs Review', totalNeedsReview),
          },
        ]
      : []),
  ]

  return (
    <FBPage
      innerPageClassName="!bg-[#fefefe]"
      containerClassName="!bg-[#fefefe] !shadow-none"
    >
      <FBPageHeader>
        <FBPageTitleRow>
          <FBPageTitle className="!text-fg">Samples</FBPageTitle>
        </FBPageTitleRow>
      </FBPageHeader>

      {brokerBlockedFromSubmitting && (
        <Alert
          color="warning"
          description="You cannot submit sample requests until a sample reviewer has been assigned to your account. Please contact your administrator."
          className="mt-2"
        />
      )}

      <TableLinkStyleProvider variant="plain">
        <FBPageTableContainer
          className="flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]"
          style={{
            height: 'calc(var(--base-page-table-height) - 10px)',
          }}
        >
          <div className="shrink-0 flex items-center justify-between gap-4 border-b border-neutral-200 bg-white px-4 py-3">
            <PillTabs<SamplesTab>
              tabs={tabs}
              activeKey={selectedTab}
              onChange={(value) => {
                navigate(`/samples/${value}/`)
              }}
              idPrefix="samples"
              aria-label="Samples views"
              size="lg"
              className="w-fit"
            />
            {!companyData?.sample_customer_service_email ||
            brokerBlockedFromSubmitting ? (
              <OverlayTrigger
                placement="bottom"
                overlay={
                  <Tooltip id="sample-request-disabled-tooltip">
                    {brokerBlockedFromSubmitting
                      ? 'You cannot submit sample requests until a sample reviewer has been assigned to your account. Please contact your administrator.'
                      : 'Configure Sample Customer Service Email in Settings → Company to enable sample requests'}
                  </Tooltip>
                }
              >
                <span>
                  <FbButton
                    disabled
                    className="!bg-brand-bright !text-white hover:!bg-brand-active pointer-events-none"
                  >
                    Place New Request
                  </FbButton>
                </span>
              </OverlayTrigger>
            ) : (
              <FbButton
                className="!bg-brand-bright !text-white hover:!bg-brand-active"
                onClick={handleNewRequest}
              >
                Place New Request
              </FbButton>
            )}
          </div>
          {selectedTab === 'all' && !isLimited && (
            <SamplesTable
              tableKey="all-samples-table"
              onEditRequest={handleEditRequest}
              onPreviewRequest={handlePreviewRequest}
              setTotalRowsCount={setTotalSamples}
            />
          )}
          {selectedTab === 'my' && (
            <SamplesTable
              tableKey="my-samples-table"
              onEditRequest={handleEditRequest}
              onPreviewRequest={handlePreviewRequest}
              setTotalRowsCount={setTotalMySamples}
              myRequestsOnly
            />
          )}
          {selectedTab === 'needs-review' && showNeedsReviewTab && (
            <SamplesTable
              tableKey="needs-review-samples-table"
              onEditRequest={handleEditRequest}
              onPreviewRequest={handlePreviewRequest}
              setTotalRowsCount={setTotalNeedsReview}
              statusFilter="pending_review"
              needsMyReview
            />
          )}
        </FBPageTableContainer>
      </TableLinkStyleProvider>

      {showFormModal && (
        <SampleRequestFormModal
          show={showFormModal}
          onHide={handleCloseModal}
          sampleRequestId={editingSampleId}
        />
      )}

      {showPreviewModal && (
        <SampleRequestPreviewModal
          show={showPreviewModal}
          onHide={handleClosePreviewModal}
          sampleRequestId={previewingSampleId}
        />
      )}

      {showReviewModal && (
        <SampleRequestReviewModal
          show={showReviewModal}
          onHide={handleCloseReviewModal}
          sampleRequestId={reviewingSampleId}
          onSuccess={() => {
            void queryClient.invalidateQueries({
              predicate: (query) =>
                typeof query.queryKey[0] === 'string' &&
                query.queryKey[0].includes('samples-table'),
            })
          }}
        />
      )}
    </FBPage>
  )
}
